import { PhotoIcon } from "@heroicons/react/24/outline";
import { Expand, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { hasDownloadLink } from "@/lib/fileLinks";

const FOCUSABLE = 'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

const UNITS = ['B', 'KB', 'MB', 'GB', 'TB'];

const ImageFieldType = ({ value, className, accent = '#ea580c', accentHover = '#f97316', accentBackground = '#fff7ed' }) => {
    function humanFileSize(size) {
        // An image record without a size used to render "NaN undefined".
        const bytes = Number(size);
        if (!Number.isFinite(bytes) || bytes < 0) return 'Unknown size';
        const i = bytes === 0 ? 0 : Math.min(Math.floor(Math.log(bytes) / Math.log(1000)), UNITS.length - 1);
        return +((bytes / Math.pow(1000, i)).toFixed(2)) * 1 + ' ' + UNITS[i];
    }

    const [fullscreen, setFullscreen] = useState(false);
    const [broken, setBroken] = useState(false);
    const triggerRef = useRef(null);
    const dialogRef = useRef(null);
    const closeRef = useRef(null);

    const label = value?.filename || 'image';
    // Only a presigned link is a src or a download; a reference the server
    // did not link (see hasDownloadLink) shows the placeholder and no anchor.
    const link = hasDownloadLink(value) ? value.link : null;
    const unavailable = broken || link === null;

    // A new src deserves a fresh verdict; without this a single failure stuck
    // the placeholder on for every later image the component was reused for.
    useEffect(() => { setBroken(false); }, [link]);

    const close = useCallback(() => setFullscreen(false), []);

    // A dialog that can only be dismissed with the mouse is a keyboard trap.
    // Escape closes it, Tab is kept inside it, and focus is handed to the close
    // button on open and back to the trigger on close — WCAG 2.1.1 Keyboard,
    // 2.1.2 No Keyboard Trap and 2.4.3 Focus Order.
    useEffect(() => {
        if (!fullscreen) return;
        const opener = triggerRef.current;
        closeRef.current?.focus();

        function onKeyDown(event) {
            if (event.key === 'Escape') {
                event.stopPropagation();
                close();
                return;
            }
            if (event.key !== 'Tab') return;
            const focusable = Array.from(dialogRef.current?.querySelectorAll(FOCUSABLE) ?? []);
            if (focusable.length === 0) return;
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        }

        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('keydown', onKeyDown);
            opener?.focus();
        };
    }, [fullscreen, close]);

    return (
        <div className={`flex flex-col w-full overflow-hidden bg-white border rounded-md shadow-sm ${className ?? ''}`}>
            {!value && (
                <p className="px-4 py-3 text-sm text-gray-500">No image attached.</p>
            )}
            {value && (
            <>
            {/* The expand affordance used to be a click handler on a div, which
                no keyboard or screen-reader user could reach. It is a real
                button now, so it is focusable, has a role and carries a name. */}
            <button
                type="button"
                ref={triggerRef}
                onClick={() => setFullscreen(true)}
                disabled={unavailable}
                aria-label={`View ${label} full screen`}
                className="relative block w-full group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-gray-900 disabled:cursor-default"
            >
                {/* Spans, not divs: a <button> may only contain phrasing content,
                    so a <div> in here is invalid HTML. `block` keeps the layout
                    identical. */}
                <span className="relative block w-full aspect-video sm:aspect-[4/3] bg-gray-100">
                    {unavailable ? (
                        // A broken src otherwise left a bare alt string floating
                        // in an empty grey box with no hint of what went wrong.
                        <span className="absolute inset-0 flex flex-col items-center justify-center gap-1 text-gray-500">
                            <PhotoIcon aria-hidden="true" className="w-8 h-8" strokeWidth={1.5} />
                            <span className="text-xs">Preview unavailable</span>
                        </span>
                    ) : (
                        <img
                            src={link}
                            alt={label}
                            onError={() => setBroken(true)}
                            className="absolute inset-0 object-cover w-full h-full"
                        />
                    )}
                    {/* Overlay for hover effect — also shown on keyboard focus so
                        the affordance is not mouse-only (WCAG 1.4.13). */}
                    {!unavailable && (
                    <span className="absolute inset-0 flex items-center justify-center transition-all duration-200 bg-black bg-opacity-0 group-hover:bg-opacity-20 group-focus-visible:bg-opacity-20">
                        <Expand
                            aria-hidden="true"
                            className="text-white stroke-1 w-8 h-8 sm:w-12 sm:h-12 md:w-16 md:h-16 opacity-0 scale-95 group-hover:opacity-100 group-hover:scale-100 group-focus-visible:opacity-100 group-focus-visible:scale-100 transition-all duration-200 pointer-events-none"
                        />
                    </span>
                    )}
                </span>
            </button>

            {/* File info section */}
            {/* Wraps rather than overlapping: in a narrow tile the size used to
                print on top of the Download link. */}
            <div className="flex flex-wrap items-center justify-between w-full p-3 text-sm border-t gap-x-2 gap-y-1 sm:p-4">
                <div className="flex items-center flex-1 min-w-0">
                    <PhotoIcon aria-hidden="true" className="w-5 h-5 text-gray-500 shrink-0" strokeWidth={2} />
                    <div className="flex flex-wrap flex-1 min-w-0 ml-2 gap-x-2 sm:ml-4">
                        <span title={value.filename} className="text-xs font-medium truncate sm:text-sm">{value.filename}</span>
                        {/* gray-400 on white is ~2.5:1, under the 4.5:1 WCAG 1.4.3
                            requires of body text. gray-600 clears it. */}
                        <span className="text-xs text-gray-600 shrink-0 sm:text-sm">
                            {humanFileSize(value.size)}
                        </span>
                    </div>
                </div>
                {link !== null && (
                    <div className="shrink-0">
                        <a
                            href={link}
                            aria-label={`Download ${label}`}
                            className="inline-block px-2 py-3 -my-2 text-xs font-medium transition-colors rounded sm:text-sm"
                            style={{
                                color: accent,
                            }}
                            onMouseEnter={e => { e.currentTarget.style.color = accentHover; e.currentTarget.style.backgroundColor = accentBackground }}
                            onMouseLeave={e => { e.currentTarget.style.color = accent; e.currentTarget.style.backgroundColor = null }}
                        >
                            Download
                        </a>
                    </div>
                )}
            </div>

            {/* Fullscreen modal */}
            {fullscreen && (
                <div
                    ref={dialogRef}
                    role="dialog"
                    aria-modal="true"
                    aria-label={label}
                    onClick={(event) => { if (event.target === event.currentTarget) close(); }}
                    className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-90"
                >
                    {/* Close button */}
                    <button
                        type="button"
                        ref={closeRef}
                        onClick={close}
                        aria-label="Close full screen image"
                        className="absolute z-10 p-2 text-white transition-colors rounded-full top-4 right-4 hover:bg-white hover:bg-opacity-20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
                    >
                        <X aria-hidden="true" className="w-6 h-6 sm:w-8 sm:h-8" />
                    </button>

                    {/* Fullscreen image. `max-w-screen` is not a Tailwind
                        class, so the image had no width bound at all. */}
                    <div className="relative max-w-full max-h-screen">
                        <img
                            src={link}
                            alt={label}
                            className="object-contain max-w-full max-h-screen"
                        />
                    </div>
                </div>
            )}
            </>
            )}
        </div>
    );
};



export default ImageFieldType;
